import { ArrowRight, MoreHorizontal, Plus, Power, PowerOff, Webhook } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { WebhookDeliveriesCard } from "@/features/webhook-deliveries";
import { WebhookEndpointFormDialog } from "@/features/webhook-endpoint-form";
import { WebhookSecretDialog, type ShownSecret } from "@/features/webhook-secret-dialog";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTenants, useUpdateWebhookEndpoint, useWebhookEndpoints } from "@/lib/queries";
import type { WebhookEndpoint } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * Webhooks (ADR-022): the URLs the platform sends a tenant's events to, and the log of what it sent.
 *
 * An endpoint is a place. What it hears is set on its own page, one event at a time, because each
 * event there has its own campaign, conditions and body. A Zapier zap is what the flow is laid out
 * for: make the endpoint from the catch hook's URL, add an event, send a test so the zap sees the
 * fields, and turn the zap on.
 *
 * Admins only, as the routes are: an endpoint sends leads' names and numbers out of the platform. The
 * signing secret is shown once, when the endpoint is made, and after that it is only rotated.
 */

const TABS = ["endpoints", "log"] as const;
type Tab = (typeof TABS)[number];

export function WebhooksPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") ?? "";
  const tab: Tab = (TABS as readonly string[]).includes(requested) ? (requested as Tab) : "endpoints";
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const { data, isPending, error, refetch } = useWebhookEndpoints();
  const { data: tenants } = useTenants({ enabled: isSuperadmin });
  const update = useUpdateWebhookEndpoint();
  const [formOpen, setFormOpen] = React.useState(false);
  const [secret, setSecret] = React.useState<ShownSecret>();

  const setOn = async (endpoint: WebhookEndpoint, enabled: boolean) => {
    try {
      await update.mutateAsync({ id: endpoint.id, enabled });
      toast.success(enabled ? "Endpoint on" : "Endpoint off", endpoint.name);
    } catch (e) {
      toast.error(
        enabled ? "Could not turn it on" : "Could not turn it off",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  const newButton = (
    <Button variant="default" onClick={() => setFormOpen(true)}>
      <Plus />
      New endpoint
    </Button>
  );

  return (
    <Page>
      <PageHeader
        title="Webhooks"
        description="Send the events you choose to a Slack channel, a Zapier zap, or your CRM. HTTP deliveries are signed with an endpoint secret."
        action={newButton}
      />

      <Tabs
        value={tab}
        onValueChange={(next) => setParams(next === "endpoints" ? {} : { tab: next }, { replace: true })}
      >
        <TabsList>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="log">Delivery log</TabsTrigger>
        </TabsList>

        <TabsContent value="endpoints" className="pt-4">
          <Card className="overflow-hidden">
            {isPending ? (
              <TableSkeleton cols={5} />
            ) : error ? (
              <ErrorState
                error={error}
                title="Could not load the endpoints"
                action={
                  <Button variant="outline" onClick={() => void refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : data.length === 0 ? (
              <EmptyState
                icon={Webhook}
                title="No endpoints yet"
                description="Add a Slack channel or the URL your receiver gives you. Connect Slack under Integrations first."
                action={newButton}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                    {isSuperadmin && <TableHead>Tenant</TableHead>}
                    <TableHead>Added</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((endpoint) => (
                    <TableRow key={endpoint.id} className="[&>td]:h-12">
                      <TableCell>
                        <Link
                          to={`/webhooks/${endpoint.id}`}
                          className="font-medium text-text underline-offset-4 hover:underline"
                        >
                          {endpoint.name}
                        </Link>
                        <p className="max-w-lg truncate font-mono text-xs text-text-muted">{endpoint.kind === "slack" ? `Slack · #${endpoint.slack_channel_name ?? endpoint.slack_channel_id}` : endpoint.url}</p>
                      </TableCell>
                      <MonoCell className="text-text-muted">{endpoint.method}</MonoCell>
                      <TableCell>
                        {endpoint.enabled ? (
                          <Badge tone="success" size="sm">
                            Sending
                          </Badge>
                        ) : (
                          <Badge tone="warning" size="sm">
                            Off
                          </Badge>
                        )}
                      </TableCell>
                      {isSuperadmin && (
                        <TableCell className="text-xs text-text-muted">
                          {tenants?.find((t) => t.id === endpoint.tenant_id)?.name ?? "—"}
                        </TableCell>
                      )}
                      <TableCell className="text-xs text-text-muted">{formatDateTime(endpoint.created_at)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${endpoint.name}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onSelect={() => navigate(`/webhooks/${endpoint.id}`)}>
                              <ArrowRight />
                              Open
                            </DropdownMenuItem>
                            {endpoint.enabled ? (
                              <DropdownMenuItem onSelect={() => void setOn(endpoint, false)}>
                                <PowerOff />
                                Turn off
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onSelect={() => void setOn(endpoint, true)}>
                                <Power />
                                Turn on
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="log" className="pt-4">
          <WebhookDeliveriesCard endpoints={data ?? []} />
        </TabsContent>
      </Tabs>

      <WebhookEndpointFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onCreated={(created) => created.kind === "slack" ? navigate(`/webhooks/${created.id}`) :
          setSecret({ endpointId: created.id, name: created.name, secret: created.secret, rotated: false })
        }
      />

      {/* After a create, Done goes on to the new endpoint's page: it hears nothing until an event is
          added there. */}
      <WebhookSecretDialog
        secret={secret}
        onClose={() => {
          const shown = secret;
          setSecret(undefined);
          if (shown && !shown.rotated) navigate(`/webhooks/${shown.endpointId}`);
        }}
      />
    </Page>
  );
}
